import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { AuthService } from '@/services/auth'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const magicLinkSent = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userEmail = computed(() => user.value?.email || '')

  // Actions
  const sendMagicLink = async (email: string): Promise<void> => {
    try {
      error.value = null
      loading.value = true
      magicLinkSent.value = false
      
      const result = await AuthService.sendMagicLink(email)
      
      if (result.success) {
        magicLinkSent.value = true
      } else {
        error.value = result.message || 'Failed to send magic link'
      }
    } catch (err: any) {
      error.value = getErrorMessage(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const validateMagicLink = async (token: string): Promise<any> => {
    try {
      error.value = null
      loading.value = true
      
      const result = await AuthService.validateMagicLink(token)
      
      if (result.success && result.authToken && result.email) {
        user.value = {
          email: result.email,
          authToken: result.authToken,
          isAdmin: true
        }
      } else {
        error.value = result.message || 'Invalid magic link'
      }
      
      return result // Return the result so debug info is available
    } catch (err: any) {
      error.value = getErrorMessage(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const signOut = (): void => {
    try {
      AuthService.signOut()
      user.value = null
      error.value = null
      magicLinkSent.value = false
    } catch (err: any) {
      error.value = 'Failed to sign out'
      throw err
    }
  }

  const initializeAuth = (): void => {
    try {
      loading.value = true
      
      const authToken = AuthService.getAuthToken()
      const email = AuthService.getAdminEmail()
      
      if (authToken && email) {
        user.value = {
          email,
          authToken,
          isAdmin: true
        }
      } else {
        user.value = null
      }
    } catch (err: any) {
      console.error('Auth initialization error:', err)
      user.value = null
    } finally {
      loading.value = false
    }
  }

  const clearError = (): void => {
    error.value = null
  }

  const clearMagicLinkSent = (): void => {
    magicLinkSent.value = false
  }

  const handleTokenExpiration = (): void => {
    console.log('[AuthStore] Handling token expiration')
    signOut()
    error.value = 'Your session has expired. Please log in again.'
  }

  // Helper function to get user-friendly error messages
  const getErrorMessage = (err: any): string => {
    if (err.message) {
      return err.message
    }
    
    return 'An unexpected error occurred. Please try again.'
  }

  return {
    // State
    user,
    loading,
    error,
    magicLinkSent,
    
    // Getters
    isAuthenticated,
    userEmail,
    
    // Actions
    sendMagicLink,
    validateMagicLink,
    signOut,
    initializeAuth,
    clearError,
    clearMagicLinkSent,
    handleTokenExpiration,
  }
})